import { dynamicFilterFilter } from "app/filters/dynamicFilter";
import { translate } from "app/filters/i18nFilter";
import startCase from "lodash/startCase";
import type { FC, ReactElement } from "react";
import styled from "styled-components";
import { colorsPalettes } from "@similarweb/styles";
import isNumber from "lodash/isNumber";
import { pureNumberFilter } from "app/filters/numberFilter";
import classNames from "classnames";

const NewLostWrapper = styled.div<{ isNew: boolean }>`
    color: ${({ isNew }) => (isNew ? colorsPalettes.green["s100"] : colorsPalettes.red["s100"])};
`;

export const ChangeContainer = styled.div`
    display: inline-flex;
    align-items: center;
    column-gap: 2px;
    line-height: 1;
`;

export enum EChangeStateValues {
    New = "New",
    Lost = "Lost",
    NoChange = "NoChange",
    NoData = "NoData",
    NoDataSign = "-",
}

interface IChangeStateCellProps {
    isPercentage: boolean;
    undefinedSign?: string;
    value: IRankObjValue;
    cellFilter?: any;
    percentageChangeLimitValue?: number;
    className?: string;
}

interface IRankObjValue {
    change?: number | string;
    first?: number;
    last?: number;
    changeState?: string;
}

const NO_CHANGE_VALUE = 0;

/**
 * @deprecated Use StateChangeCell from @similarweb/pro-logic-components instead
 * @see {@link https://gitlab.int.similarweb.io/pro-frontend/pro-logic-components/-/blob/master/src/table/cells/components/views/state-change-cell/StateChangeCell.tsx}
 */

export const ChangeStateCell: FC<IChangeStateCellProps> = ({
    value,
    cellFilter = undefined,
    isPercentage,
    undefinedSign,
    percentageChangeLimitValue = 5000,
    className,
}) => {
    const getChangeValue = (rankObject: IRankObjValue): ReactElement | number => {
        const { changeState } = rankObject;
        switch (changeState) {
            case EChangeStateValues.Lost:
            case EChangeStateValues.New:
                const isNew = changeState === EChangeStateValues.New;
                const label = translate(`common.tooltip.change.${isNew ? "new" : "lost"}`);
                return <NewLostWrapper isNew={isNew}>{startCase(label.toLowerCase())}</NewLostWrapper>;
            case EChangeStateValues.NoChange:
                return NO_CHANGE_VALUE;
            case EChangeStateValues.NoData:
            case EChangeStateValues.NoDataSign:
                return <>-</>;
            default:
                const changeAfterFilter = dynamicFilterFilter()(rankObject?.change, cellFilter, undefinedSign);

                return Number(changeAfterFilter);
        }
    };

    const changeFilter = (value: number | ReactElement, isPercentage: boolean) => {
        if (isNumber(value)) {
            if (isPercentage && value !== 0) {
                return value > percentageChangeLimitValue
                    ? ` > ${pureNumberFilter(percentageChangeLimitValue)}%`
                    : pureNumberFilter(value) + "%";
            } else {
                return Math.abs(value);
            }
        }
        return value;
    };

    const getSignClass = (value: number | ReactElement) => {
        if (isNumber(value)) {
            return value < 0 ? "down2 negative" : value > 0 ? "up2 positive" : "";
        } else {
            return "";
        }
    };

    const changeSignedValue = getChangeValue(value);

    return (
        <div className={classNames("text-right changePercentage", className)}>
            <ChangeContainer className={`sw-icon-arrow-${getSignClass(changeSignedValue as number)}`}>
                {changeFilter(changeSignedValue, isPercentage)}
            </ChangeContainer>
        </div>
    );
};
